/* eslint-disable @typescript-eslint/no-misused-promises */

'use client'

import { useEffect, useRef, useState } from 'react'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import Image from 'next/image'
import { useElementVisible } from '@hooks/useElementVisible'
import { VideoCompMock } from '@components/AboutUs/AboutUsMock'
import { MUTE_AUDIO_ICON_SRC, UNMUTE_AUDIO_ICON_SRC } from '@constants/aboutUs'
import { EVENTS } from '@configs/events.config'
import { TENANT_TYPE } from '@components/Ackodrive/common/constants'
import styles from './VideoComponent.module.scss'

const VideoComponent = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const textRef = useRef<HTMLDivElement | null>(null)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const videoWrapperRef = useRef<HTMLDivElement | null>(null)
  const subTextSection = useElementVisible()
  const [showSecondIcon, setShowSecondIcon] = useState(false)

  useEffect(() => {
    const container = textRef.current
    if (!container) return

    const words = VideoCompMock?.fullText?.split(' ')
    words.forEach((word, index) => {
      const outerSpan = document.createElement('span')
      const innerSpan = document.createElement('span')
      innerSpan.textContent = `${word} `
      innerSpan.style.animationDelay = `${index * 10}ms`
      outerSpan.appendChild(innerSpan)
      container.appendChild(outerSpan)
    })
  }, [])

  useEffect(() => {
    const container = textRef.current
    if (!container) return

    let lastScrollY = window.scrollY

    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentScrollY = window.scrollY
        const scrollingDown = currentScrollY > lastScrollY
        lastScrollY = currentScrollY

        const isVisible = entry.isIntersecting

        if (isVisible) {
          container.classList.remove(styles.animateOut)
          void container.offsetWidth
          container.classList.add(styles.animateIn)
        } else if (!isVisible && !scrollingDown) {
          container.classList.remove(styles.animateIn)
          void container.offsetWidth
          container.classList.add(styles.animateOut)
        }
      },
      {
        threshold: 0.7,
      }
    )

    observer.observe(container)

    const rect = container.getBoundingClientRect()
    const fullyInView = rect.top >= 0 && rect.bottom <= window.innerHeight
    if (fullyInView) {
      container.classList.add(styles.animateIn)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const container = videoRef.current
    if (!container) return

    let lastScrollY = window.scrollY

    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentScrollY = window.scrollY
        const scrollingDown = currentScrollY > lastScrollY
        lastScrollY = currentScrollY

        const isVisible = entry.isIntersecting

        if (isVisible && scrollingDown) {
          container.classList.remove(styles.animateVideoOut)
          void container.offsetWidth
          container.classList.add(styles.animateVideoIn)
        } else if (!isVisible && !scrollingDown) {
          container.classList.remove(styles.animateVideoIn)
          void container.offsetWidth
          container.classList.add(styles.animateVideoOut)
        }

        // Play/pause logic
        if (videoRef.current) {
          if (entry.isIntersecting) {
            videoRef.current.muted = true
            void videoRef.current.play()
          } else {
            videoRef.current.pause()
            videoRef.current.currentTime = 0
            setIsAudioPlaying(false)
          }
        }
      },
      {
        threshold: 0.4,
      }
    )

    observer.observe(container)

    // If already fully in view on load
    const rect = container.getBoundingClientRect()
    const fullyInView = rect.top >= 0 && rect.bottom <= window.innerHeight
    if (fullyInView) {
      container.classList.add(styles.animateVideoIn)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleVideoClick = async () => {
    if (!showSecondIcon) setShowSecondIcon(prev => !prev)
    if (isAudioPlaying) setIsAudioPlaying(prev => !prev)
    setTimeout(() => {
      if (!videoRef.current) return
      if (isAudioPlaying) {
        videoRef.current.muted = true
        setShowSecondIcon(prev => !prev)
      } else {
        videoRef.current.muted = false
        void videoRef.current.play()
        setIsAudioPlaying(true)
      }
    }, 300)

    await trackEvent(EVENTS[TENANT_TYPE.ABOUTUS].engage.eventname, {
      ...EVENTS[TENANT_TYPE.ABOUTUS].engage.eventProps,
      journey: 'video',
    })
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div ref={textRef} className={styles.animatedText} />
        <span
          ref={subTextSection.ref}
          className={`${styles.subText} ${subTextSection.isVisible ? styles.visible : ''}`}>
          {VideoCompMock?.subText}
        </span>
      </div>

      <div className={styles.videoWrapper} ref={videoWrapperRef}>
        <video
          ref={videoRef}
          className={styles.video}
          src={VideoCompMock?.videoSrc}
          loop
          playsInline
          muted
          preload="auto"
          onClick={handleVideoClick}
        />
        {!isAudioPlaying && (
          <button type="button" aria-label="Play Video" className={styles.playButton} onClick={handleVideoClick}>
            <Image
              src={showSecondIcon ? UNMUTE_AUDIO_ICON_SRC : MUTE_AUDIO_ICON_SRC}
              alt="Mute/Unmute Video"
              width={80}
              height={80}
              priority
            />
          </button>
        )}
      </div>
    </div>
  )
}

export default VideoComponent
